import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Field,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  Select,
  Spinner,
} from "../../../design-system";
import {
  addContractTerms,
  getHolidayGroups,
  getVacationPolicies,
  getWorkSchedules,
} from "../../../helpers/kronaly_helper";
import { CHANGE_TYPES } from "./formatters";

const todayYmd = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const initial = () => ({
  effective_from: todayYmd(),
  change_type: "other",
  note: "",
  job_title: "",
  salary_amount: "",
  salary_currency: "EUR",
  work_schedule_id: "",
  holiday_group_ids: [],
  vacation_policy_id: "",
});

const AddTermsModal = ({ open, contractId, onClose, onCreated }) => {
  const [form, setForm] = useState(initial);
  const [schedules, setSchedules] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [vacations, setVacations] = useState([]);
  const [loadingCatalogs, setLoadingCatalogs] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setForm(initial());
    setError(null);
    setLoadingCatalogs(true);
    Promise.all([getWorkSchedules(), getHolidayGroups(), getVacationPolicies()])
      .then(([a, b, c]) => {
        setSchedules(a?.data ?? a ?? []);
        setHolidays(b?.data ?? b ?? []);
        setVacations(c?.data ?? c ?? []);
      })
      .catch((e) =>
        setError(typeof e === "string" ? e : e?.message || "No se pudieron cargar catálogos."),
      )
      .finally(() => setLoadingCatalogs(false));
  }, [open]);

  const handle = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = {
        effective_from: form.effective_from,
        change_type: form.change_type,
      };
      if (form.note.trim()) payload.note = form.note.trim();
      if (form.job_title.trim()) payload.job_title = form.job_title.trim();
      if (form.salary_amount !== "") payload.salary_amount = Number(form.salary_amount);
      if (form.salary_currency.trim()) payload.salary_currency = form.salary_currency.trim();
      if (form.work_schedule_id) payload.work_schedule_id = form.work_schedule_id;
      if (form.holiday_group_ids.length > 0) payload.holiday_group_ids = form.holiday_group_ids;
      if (form.vacation_policy_id) payload.vacation_policy_id = form.vacation_policy_id;

      const r = await addContractTerms(contractId, payload);
      onCreated(r?.data ?? r);
      onClose();
    } catch (err) {
      setError(typeof err === "string" ? err : err?.message || "No se pudo crear el anexo.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Anexar nuevos términos" size="lg">
      <form onSubmit={onSubmit}>
        <ModalBody>
          {error && <div className="kr-mb-3"><Alert tone="danger">{error}</Alert></div>}
          <p className="kr-text-muted kr-fs-12 kr-mb-3">
            El terms vigente se cerrará automáticamente el día anterior a{" "}
            <strong>{form.effective_from || "—"}</strong>. Solo informa los campos que cambian; los
            demás se heredan del terms previo.
          </p>
          {loadingCatalogs ? (
            <div className="kr-text-center kr-py-3"><Spinner size={16} /></div>
          ) : (
            <>
              <div className="kr-grid kr-gap-3" style={{ gridTemplateColumns: "1fr 1fr" }}>
                <Field label="Vigencia desde" required>
                  <Input type="date" value={form.effective_from} onChange={handle("effective_from")} required />
                </Field>
                <Field label="Tipo de cambio" required>
                  <Select value={form.change_type} onChange={handle("change_type")} required>
                    {CHANGE_TYPES.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </Select>
                </Field>
              </div>
              <div className="kr-mt-3">
                <Field label="Nota">
                  <Input type="textarea" rows={2} value={form.note} onChange={handle("note")} />
                </Field>
              </div>
              <div className="kr-grid kr-gap-3 kr-mt-3" style={{ gridTemplateColumns: "1fr 140px 100px" }}>
                <Field label="Puesto / cargo">
                  <Input type="text" value={form.job_title} onChange={handle("job_title")} maxLength={120} />
                </Field>
                <Field label="Salario anual">
                  <Input type="number" step="0.01" min={0} value={form.salary_amount} onChange={handle("salary_amount")} />
                </Field>
                <Field label="Moneda">
                  <Input type="text" value={form.salary_currency} onChange={handle("salary_currency")} maxLength={3} />
                </Field>
              </div>
              <div className="kr-grid kr-gap-3 kr-mt-3" style={{ gridTemplateColumns: "1fr 1fr 1fr" }}>
                <Field label="Cambiar horario">
                  <Select value={form.work_schedule_id} onChange={handle("work_schedule_id")}>
                    <option value="">— sin cambio —</option>
                    {schedules.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
                  </Select>
                </Field>
                <Field label="Cambiar festivos" help="Mantén Ctrl/⌘ para varios; déjalo vacío para no tocar.">
                  <select
                    multiple
                    value={form.holiday_group_ids}
                    onChange={(e) => {
                      const selected = Array.from(e.target.selectedOptions).map((o) => o.value);
                      setForm((f) => ({ ...f, holiday_group_ids: selected }));
                    }}
                    style={{
                      width: "100%",
                      minHeight: 64,
                      border: "1px solid var(--kr-border)",
                      borderRadius: 6,
                      padding: 8,
                      font: "400 13px/1.4 var(--kr-font-sans)",
                      color: "var(--kr-fg-strong)",
                      background: "var(--kr-bg-surface)",
                    }}
                  >
                    {holidays.map((h) => (
                      <option key={h.id} value={h.id}>
                        {h.name}{h.kind && h.kind !== "other" ? ` (${h.kind})` : ""}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Cambiar vacaciones">
                  <Select value={form.vacation_policy_id} onChange={handle("vacation_policy_id")}>
                    <option value="">— sin cambio —</option>
                    {vacations.map((v) => (<option key={v.id} value={v.id}>{v.name}</option>))}
                  </Select>
                </Field>
              </div>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" type="submit" disabled={saving}>
            {saving && <Spinner size={12} color="#fff" />}
            Crear anexo
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default AddTermsModal;
